import React from 'react';
import { FormControl } from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import classNames from 'clsx';

import { ControlContainer } from 'app/components/ControlContainer';
import { Caption } from 'app/components/Caption';
import { SelectWithFilter } from 'app/components/SelectWithFilter';
import { SelectControlProps } from './SelectWithFilterControl.interfaces';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import isLabelFitToInput from 'app/shared/helpers/isLabelFitToInput';
import { FormsEnum, LabelEnum } from 'app/shared/enums';
import { useStyles } from './SelectWithFilterControl.styles';
import { useSelectWithFilterControl } from 'app/shared/hooks/controls/useSelectWithFilterControl';

export default function SelectWithFilterControl(props: SelectControlProps) {
  const classes = useStyles();

  const {
    answerGroups,
    className,
    cols,
    dictionary,
    helperText,
    inlineTooltip,
    input,
    isLoading,
    meta,
    tooltip,
    width,
    placeholder,
    duplicateLabel,
    readonlyInput,
    labelAboveInputField,
  } = props;

  const {
    error,
    focused,
    getHasDependsOnWithoutValue,
    getHasDictionaryWithoutData,
    getIsDisabled,
    getLabel,
    handleBlur,
    handleChange,
    handleClick,
    handleFocus,
    readOnly,
  } = useSelectWithFilterControl(props);

  const renderControl = () => {
    return (
      <SelectWithFilter
        {...input}
        classes={{
          root: classNames(classes.root, { [classes.readOnly]: readOnly }),
        }}
        value={getIsDisabled() ? '' : input.value}
        dictionary={dictionary}
        answerGroups={answerGroups}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={!readOnly ? handleFocus : undefined}
        label={
          duplicateLabel
            ? placeholder
            : !labelAboveInputField && isLabelFitToInput(getLabel(), width)
            ? getLabel()
            : undefined
        }
        focused={focused}
        onBlur={handleBlur}
        error={error}
        disabled={getIsDisabled()}
        readOnly={readOnly}
        cols={cols}
        width={width}
        name={input.name}
        isTravelResultsFilters={meta.form === FormsEnum.TravelResultsFilters}
        inputProps={{
          placeholder: focused ? undefined : placeholder,
          'aria-labelledby': `${input.name}-label`,
          readOnly: readonlyInput,
          tabIndex: readOnly ? -1 : 0,
        }}
      />
    );
  };

  const renderCaption = () => {
    return inlineTooltip && (focused || input.value) && !getIsDisabled() ? (
      <Caption
        classes={{
          root: classNames(
            {
              [classes.captionWithCheckIcon]: input.value,
            },
            classes.caption
          ),
        }}
        text={inlineTooltip}
      />
    ) : null;
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <FormControl className={className} fullWidth disabled={getIsDisabled()}>
        <ControlContainer
          inputId={input.name}
          tooltip={tooltip}
          disabled={getIsDisabled()}
          meta={meta}
          width={width}
          hoverTooltipText={
            getHasDependsOnWithoutValue() || getHasDictionaryWithoutData()
              ? LabelEnum.FillPreviousQuestions
              : ''
          }
          helperText={helperText}
          label={
            labelAboveInputField || duplicateLabel
              ? getLabel()
              : !isLabelFitToInput(getLabel(), width)
              ? getLabel()
              : ''
          }
          focused={focused}
        >
          {renderControl()}
          {renderCaption()}
        </ControlContainer>
      </FormControl>
    </ReactPlaceholder>
  );
}
